import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  checkAssessmentExists,
  archiveAssessment,
  createTPRAAssessment,
  type ExistingAssessment,
  type CreateTPRAAssessmentRequest,
} from "@/modules/agent/requests/tpra-assessment";
import {
  changeBusinessOwnerFormState,
  completeAssessmentIntake,
} from "@/modules/third-party-risk/requests/intake-form-v2";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";

type ModalStep = "none" | "exists" | "archive";

interface UseAcceptFlowParams {
  formId: string;
  vendorName: string;
  productName: string;
}

export function useAcceptFlow({
  formId,
  vendorName,
  productName,
}: UseAcceptFlowParams) {
  const navigate = useNavigate();

  const [modalStep, setModalStep] = useState<ModalStep>("none");
  const [existingAssessment, setExistingAssessment] =
    useState<ExistingAssessment | null>(null);
  const [memberHasAccess, setMemberHasAccess] = useState(false);
  const [archiveConfirmed, setArchiveConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedAssessmentId, setAcceptedAssessmentId] = useState<
    string | null
  >(null);

  const createAndApprove = useCallback(async (): Promise<string> => {
    const request: CreateTPRAAssessmentRequest = {
      type: "multi_file_vendor_assessment",
      multi_file_vendor_assessment: {
        doc_type: "json",
        response_quality: "accuracy",
        source_urls: [],
        vendor_name: vendorName,
        product_name: productName,
        selected_control_sets: [],
        vendor_filled_intake_form: false,
      },
    };

    await changeBusinessOwnerFormState(formId, {
      action: "approve",
    });

    const result = await createTPRAAssessment(request);
    const assessmentId = result.assessment.id;

    await completeAssessmentIntake(assessmentId);

    return assessmentId;
  }, [formId, vendorName, productName]);

  const startAcceptFlow = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await checkAssessmentExists(vendorName, productName);

      if (result && result.assessments.length > 0) {
        setExistingAssessment(result.assessments[0]);
        setMemberHasAccess(result.member_has_access);
        setModalStep("exists");
        setIsLoading(false);
      } else {
        const newAssessmentId = await createAndApprove();
        setIsLoading(false);
        navigate(`/${ROUTES.AGENT}/${newAssessmentId}`);
      }
    } catch (err) {
      addNotification({
        type: "error",
        message:
          err instanceof Error ? err.message : "Failed to process acceptance",
      });
      setIsLoading(false);
    }
  }, [vendorName, productName, createAndApprove, navigate]);

  const handleViewExisting = useCallback(() => {
    if (!existingAssessment) return;
    navigate(`/${ROUTES.AGENT}/${existingAssessment.id}`);
  }, [existingAssessment, navigate]);

  const handleStartNew = useCallback(() => {
    setArchiveConfirmed(false);
    setModalStep("archive");
  }, []);

  const handleArchiveProceed = useCallback(async () => {
    if (!existingAssessment) return;
    setIsLoading(true);
    try {
      await archiveAssessment(existingAssessment.id);
      const newAssessmentId = await createAndApprove();
      setIsLoading(false);
      navigate(`/${ROUTES.AGENT}/${newAssessmentId}`);
    } catch (err) {
      addNotification({
        type: "error",
        message:
          err instanceof Error ? err.message : "Failed to archive and create",
      });
      setIsLoading(false);
    }
  }, [existingAssessment, createAndApprove, navigate]);

  const closeModals = useCallback(() => {
    setModalStep("none");
    setExistingAssessment(null);
    setArchiveConfirmed(false);
  }, []);

  const handleBackFromArchive = useCallback(() => {
    setArchiveConfirmed(false);
    setModalStep("exists");
  }, []);

  return {
    modalStep,
    existingAssessment,
    memberHasAccess,
    archiveConfirmed,
    setArchiveConfirmed,
    isLoading,
    acceptedAssessmentId,
    startAcceptFlow,
    handleViewExisting,
    handleStartNew,
    handleArchiveProceed,
    closeModals,
    handleBackFromArchive,
  };
}
